import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authApi } from "../../services/api/authApi";
import type { CustomRequestConfig } from "../../types/api";
import { isTokenValid } from "../../utils/token";
import { config } from "../../config/environment";
import type {
  IAuthUser,
  IAuthResponse,
  ILoginCredentialsBase,
  ILoginCredentials,
  IApiError,
} from "../../types/auth";

interface AuthState {
  user: IAuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const storedToken = localStorage.getItem("token");
//console.log("Stored Token:", storedToken);
//console.log("Is Token Valid:", isTokenValid(storedToken));

const initialState: AuthState = {
  user: null,
  token: storedToken,
  isAuthenticated: storedToken ? isTokenValid(storedToken) : false,
  loading: false, // Changed to false initially
  error: null,
};

// Remove invalid tokens from localStorage
if (storedToken && !isTokenValid(storedToken)) {
  console.log("Invalid token found. Removing from localStorage.");
  localStorage.removeItem("token");
}

export const login = createAsyncThunk(
  "auth/login",
  async (credentials: ILoginCredentialsBase, { rejectWithValue }) => {
    try {
      const fullCredentials: ILoginCredentials = {
        ...credentials,
        clinicId: config.clinic.id,
      };
      const response = await authApi.login(fullCredentials);
      return response;
    } catch (error) {
      const apiError = error as IApiError;
      return rejectWithValue(apiError.message || "Login failed");
    }
  }
);

export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;

      if (!token || !isTokenValid(token)) {
        localStorage.removeItem("token");
        throw new Error("Invalid or expired token");
      }

      const response = await authApi.validateToken();
      return response;
    } catch (error) {
      const apiError = error as IApiError;
      return rejectWithValue(apiError.message || "Token validation failed");
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("No token available");
      }

      // Pass the Authorization token in headers
      const config: CustomRequestConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await authApi.getCurrentUser(config); // Now passing config
      return response; // Returns user data
    } catch (error) {
      const apiError = error as IApiError;
      return rejectWithValue(apiError.message || "Failed to fetch user");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      localStorage.removeItem("token");
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { user, token } = action.payload as IAuthResponse;
        if (isTokenValid(token)) {
          state.loading = false;
          state.isAuthenticated = true;
          state.user = user;
          state.token = token;
          localStorage.setItem("token", token);
        } else {
          state.loading = false;
          state.error = "Invalid token received";
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Token validation
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        localStorage.removeItem("token");
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
